import React, { useState } from 'react';
import { ChevronDown, Calendar } from 'lucide-react';

export type PeriodPreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_3_months'
  | 'last_6_months'
  | 'custom';

interface Props {
  startDate: string;
  endDate: string;
  onDateChange: (start: string, end: string, preset: PeriodPreset) => void;
}

const presets: { value: PeriodPreset; label: string }[] = [
  { value: 'this_month', label: 'Ce mois' },
  { value: 'last_month', label: 'Mois dernier' },
  { value: 'this_quarter', label: 'Ce trimestre' },
  { value: 'last_3_months', label: '3 derniers mois' },
  { value: 'last_6_months', label: '6 derniers mois' },
  { value: 'custom', label: 'Personnalise' },
];

export function getPresetDates(preset: PeriodPreset): { start: string; end: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  switch (preset) {
    case 'this_month': {
      const start = new Date(y, m, 1);
      const end = new Date(y, m + 1, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'last_month': {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'this_quarter': {
      const qStart = Math.floor(m / 3) * 3;
      const start = new Date(y, qStart, 1);
      const end = new Date(y, qStart + 3, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'last_3_months': {
      const start = new Date(y, m - 2, 1);
      const end = new Date(y, m + 1, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'last_6_months': {
      const start = new Date(y, m - 5, 1);
      const end = new Date(y, m + 1, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    default:
      return { start: fmt(new Date(y, m, 1)), end: fmt(new Date(y, m + 1, 0)) };
  }
}

function fmt(d: Date): string {
  return d.toISOString().split('T')[0];
}

const StatisticsPeriodSelector: React.FC<Props> = ({ startDate, endDate, onDateChange }) => {
  const [open, setOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<PeriodPreset>('last_month');
  const [showCustom, setShowCustom] = useState(false);

  const handlePreset = (preset: PeriodPreset) => {
    setActivePreset(preset);
    if (preset === 'custom') {
      setShowCustom(true);
      setOpen(false);
      return;
    }
    setShowCustom(false);
    const { start, end } = getPresetDates(preset);
    onDateChange(start, end, preset);
    setOpen(false);
  };

  const activeLabel = presets.find((p) => p.value === activePreset)?.label || 'Mois dernier';

  return (
    <div className="flex items-center space-x-3">
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
        >
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{activeLabel}</span>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handlePreset(preset.value)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    activePreset === preset.value
                      ? 'bg-rose-50 text-rose-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {showCustom && (
        <div className="flex items-center space-x-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => onDateChange(e.target.value, endDate, 'custom')}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none"
          />
          <span className="text-gray-400 text-sm">-</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onDateChange(startDate, e.target.value, 'custom')}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none"
          />
        </div>
      )}
    </div>
  );
};

export default StatisticsPeriodSelector;
